import React, { useEffect, useState, useRef } from 'react';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock';
import Flickity from 'flickity';
import 'flickity-imagesloaded';
import ModalUpsellProductDetails from './ModalUpsellProductDetails';
import ModalUpsellCalculatePercentSale from './ModalUpsellCalculatePercentSale';
import ModalUpsellBadge from './ModalUpsellBadge';
import ModalUpsellTextButton from './ModalUpsellTextButton';
import { updateCart } from '../utils/update-cart';
import { getCart } from '../utils/get-cart';

const ModalUpsell = () => {
  const {
    productCondition,
    title,
    titleMobile,
    productUpsells,
  } = window.newCartUpsellConfig;

  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [indexAdded, setIndexAdded] = useState(-1);
  const [itemAdded, setItemAdded] = useState([]);
  const [carouselCurrent, setCarouselCurrent] = useState(null);
  const [initHeight, setInitHeight] = useState(null);
  const [hasCalcHeight, setHasCalcHeight] = useState(true);
  const [cartItems, setCartItems] = useState(window.theme.cart ? window.theme.cart.items : []);
  const [layoutActive, setLayoutActive] = useState('product');
  const [currentProduct, setCurrentProduct] = useState(productUpsells[0]);

  const modalRef = useRef(null);
  const carouselProductRef = useRef(null);
  const productContentRef = useRef(null);
  const flktyRef = useRef(null);
  const productCarouselRef = useRef(null);

  if (!productUpsells?.length || !productCondition) return null;

  const handleAddToCart = async (product) => {
    setIsLoading(true);
    const response = await fetch(`/products/${product.handle}.js`);
    const productJson = await response.json();
    const selectedVariantId = Number(product.initVariantId);
    const selectedVariant = productJson.variants.find(variant => variant.id === selectedVariantId) || productJson.variants[0];

    const items = [{
      id: product.initVariantId,
      properties: {
        _new_upsell: product.handle,
        _upsell: product.handle,
        _original_price: selectedVariant.price,
        _upsell_conditional_id: product.conditionalProductId,
        _upsell_conditional_discount: product.conditionalDiscount,
        _upsell_conditional_hash: product.conditionalHash
      },
      quantity: 1,
    }];

    const newCart = await getCart();
    const productOfferInCart = newCart?.items.filter(item => item.properties?._new_upsell) || [];

    if (productOfferInCart.length) {
      await handleRemoveItem(productOfferInCart);
    }

    if (newCart?.items?.some(item => item.product_id.toString() === productCondition.id.toString())) {
      await window.sideCart.handleAddToCartEvent(items, false, 0, product.handle, null, false, {});
    } else {
      window.location.reload();
    }
  };

  const handleRemoveItem = async (products) => {
    if (!products) return;
    const isAutoUpsell = products.find(product => {
      const variantId = product.variant_id || product.initVariantId
      return cartItems.find(item => item.variant_id === variantId && item.properties && item.properties._upsell && item.properties._autoupsell)
    });

    if (isAutoUpsell) {
      window.localStorage.setItem('removedAutoUpsell', Date.now() + 1000 * 60 * 60 * 24);
    }

    const updates = products.reduce((acc, item) => {
      acc[item.variant_id || item.initVariantId] = 0;
      return acc;
    }, {});

    try {
      const updatedCart = await updateCart({ updates });
      if (updatedCart?.items) {
        setCartItems(updatedCart.items);
        window.sideCart.updateCart(updatedCart);
      }
    } catch (error) {
      console.error('Error updating the cart:', error);
    }
  };

  const handleActiveDetails = (product) => {
    setLayoutActive('detail');
    setCurrentProduct(product);
  };

  useEffect(() => {
    const handleOpenModal = () => setIsActive(true);
    const handleCartUpdate = (event) => {
      setCartItems(event.detail);
      setIsLoading(false);
    };

    document.addEventListener('open-modal-upsell', handleOpenModal);
    document.addEventListener('cart:updated', handleCartUpdate);

    return () => {
      document.removeEventListener('open-modal-upsell', handleOpenModal);
      document.removeEventListener('cart:updated', handleCartUpdate);
    };
  }, []);

  useEffect(() => {
    if (carouselProductRef.current) {
      const flkty = new Flickity(carouselProductRef.current, {
        prevNextButtons: true,
        pageDots: true,
        groupCells: true,
        contain: true,
        watchCSS: true,
      });
      flktyRef.current = flkty;
      setCarouselCurrent(flkty)
      setInitHeight(flkty.maxCellHeight)

      let initialX = null;
      let initialY = null;

      const handleTouchStart = (e) => {
        initialX = e.touches[0].clientX;
        initialY = e.touches[0].clientY;
      };

      const handleTouchMove = (e) => {
        if (initialX == null || initialY == null) return;

        const { clientX: currentX, clientY: currentY } = e.touches[0];
        const diffX = initialX - currentX;
        const diffY = initialY - currentY;

        if (Math.abs(diffX) > Math.abs(diffY)) {
          clearAllBodyScrollLocks();
          document.body.style.overflow = 'hidden';
        }
      };

      const handleTouchEnd = () => {
        document.body.style.overflow = 'auto';
        disableBodyScroll(productCarouselRef.current);
      };

      carouselProductRef.current.addEventListener('touchstart', handleTouchStart);
      carouselProductRef.current.addEventListener('touchmove', handleTouchMove);
      carouselProductRef.current.addEventListener('touchend', handleTouchEnd);

      return () => {
        carouselProductRef.current.removeEventListener('touchstart', handleTouchStart);
        carouselProductRef.current.removeEventListener('touchmove', handleTouchMove);
        carouselProductRef.current.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, []);

  useEffect(() => {
    if (isActive) {
      disableBodyScroll(productCarouselRef.current);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [isActive]);

  useEffect(() => {
    if (layoutActive === 'detail') {
      disableBodyScroll(productContentRef.current);
    }
  }, [layoutActive]);

  useEffect(() => {
    if (cartItems.length) {
      const listProductId = new Set(cartItems.map(item => item.product_id));
      const matchingItems = productUpsells.filter(item => listProductId.has(item.id));
      const matchingItemsIndex = productUpsells.findIndex(item => listProductId.has(item.id));

      setItemAdded(matchingItems);
      setIndexAdded(matchingItemsIndex);

      if (carouselCurrent) {
        if (hasCalcHeight && matchingItemsIndex !== -1) {
          carouselCurrent.viewport.style.height = `${initHeight + 34}px`;
          setHasCalcHeight(false);
        } else {
          carouselCurrent.viewport.style.height = `${initHeight}px`;
          setHasCalcHeight(true);
        }
      }
    }
  }, [cartItems, carouselCurrent]);

  if (!productUpsells.length) return null;

  return (
    <div ref={modalRef} className={`modal-upsell ${isActive ? 'active' : ''}`}>
      <div className="fader fader--modal-upsell" />
      <div
        className={`modal modal--modal-upsell ${
          isActive ? 'modal--visible' : ''
        } modal-upsell--products-${productUpsells.length} ${
          layoutActive === 'product'
            ? 'modal-upsell--products'
            : 'modal-upsell--details'
        }`}
      >
        <div
          className={`modal-upsell__content-wrapper ${
            layoutActive === 'product'
              ? 'modal-upsell__content--products'
              : 'modal-upsell__content--details'
          }`}
        >
          <div className="modal-upsell__products" ref={productCarouselRef}>
            <div className="modal-upsell__header">
              {title && (
                <h3 className={`modal-upsell__heading ${titleMobile ? 'modal-upsell__heading-desktop' : ''}`}>
                  {title}
                </h3>
              )}
              {titleMobile && (
                <h3 className="modal-upsell__heading modal-upsell__heading-mobile">
                  {titleMobile}
                </h3>
              )}
            </div>
            <div className="modal-upsell__content">
              <div ref={carouselProductRef} className="modal-upsell__carousel">
                {productUpsells.map((product, index) => (
                  <div className="modal-upsell__carousel-item product-card" key={product.handle}>
                    {product.featuredImage?.src ? (
                      <div className="modal-upsell__product-wrapper-image product-card__image">
                        <ModalUpsellBadge product={product} />
                        <img
                          className="modal-upsell__product-image"
                          alt={product.featuredImage.alt}
                          src={product.featuredImage.src}
                          width={product.featuredImage.width}
                          height={product.featuredImage.height}
                        />
                      </div>
                    ) : (
                      <div className="modal-upsell__product-image-default" />
                    )}
                    <div>
                      <h4 className="modal-upsell__product-title">{product.title}</h4>
                      <div className="okeReviews-ratingWrapper">
                        <div
                          data-oke-reviews-product-listing-rating
                          dangerouslySetInnerHTML={{ __html: product.metafields.okendoListing }}
                        />
                        {product.metafields.reviewCount > 0 && (
                          <span className="okeReviews-reviewCount">
                            <strong>(</strong>
                            {product.metafields.reviewCount}
                            <strong>)</strong>
                          </span>
                        )}
                      </div>
                      <div
                        className={`modal-upsell__product-price product-card__price ${
                          product.compareAtPriceMin.value > 0 && product.compareAtPriceMin.value > product.price.value
                            ? 'product-card__price--discounted'
                            : ''
                        }`}
                      >
                        {product.compareAtPriceMin.value > 0 && (
                          <s>{product.compareAtPriceMin.text}</s>
                        )}
                        <span>
                          {product.compareAtPriceMin.value > 0 && product.price.value === 0
                            ? 'Free'
                            : product.price.text}
                        </span>
                        <ModalUpsellCalculatePercentSale
                          price={product.price.value}
                          comparePrice={product.compareAtPriceMin.value}
                        />
                      </div>
                      {product.metafields.shortDescription && (
                        <div className="modal-upsell__product-description">
                          {product.metafields.shortDescription}
                        </div>
                      )}
                      <button
                        className="modal-upsell__view-details"
                        onClick={() => handleActiveDetails(product)}
                      >
                        View product details
                      </button>
                      {product.metafields.dateOfferEnd && (
                        <p className="modal-upsell__offer-text">
                          Offer ends {product.metafields.dateOfferEnd}
                        </p>
                      )}
                    </div>
                    <div className="modal-upsell__button">
                      <button
                        onClick={() => handleAddToCart(product)}
                        className={`modal-upsell__add btn btn--primary btn--block btn--add-to-cart ${
                          isLoading ? 'modal-upsell__add-adding' : ''
                        }`}
                        disabled={!product.available || indexAdded === index || product.metafields.outDateOffer}
                      >
                        {isLoading ? (
                          <div className="modal-upsell__loading">
                            <div className="modal-upsell__loading-content">
                              <div className="modal-upsell__loading-spin" />
                            </div>
                          </div>
                        ) : (
                          <ModalUpsellTextButton product={product} index={index} indexAdded={indexAdded} />
                        )}
                      </button>
                      {indexAdded !== -1 && (
                        <button
                          onClick={() => handleRemoveItem([product])}
                          className={`modal-upsel__remove-item ${
                            indexAdded !== index ? 'modal-upsel__remove-fake' : ''
                          }`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-upsell__bottom">
                <a href="/checkout" className="modal-upsell__checkout btn btn--primary">
                  Continue to Checkout
                </a>
              </div>
            </div>
          </div>
          <div className="modal-upsell__product-details" ref={productContentRef}>
            <ModalUpsellProductDetails
              key={currentProduct?.id}
              product={currentProduct}
              setLayoutActive={setLayoutActive}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalUpsell;
